import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import {
  tryToGetReviewsReport,
  tryToDownloadReviewsCsv,
  tryToDownloadReviewsXlsx,
  tryToHarvestNow,
  tryToBackfillMonths,
  PeriodKey,
  ReportResponse,
} from './reviewsApi';

export type ReviewsFilters = {
  period: PeriodKey;
  minScore?: number | null;
  from?: string;
  to?: string;
  sinceLastReport?: boolean;
  mark?: boolean;
};

interface IReviewsState {
  // report data
  report?: ReportResponse | null;
  loading: boolean;
  error?: string | null;

  // last blobs (optional: UI can use them directly)
  lastCsvBlobUrl?: string | null;
  lastXlsxBlobUrl?: string | null;

  // last harvest/backfill info
  lastHarvest?: { inserted: number; skipped: number; since?: string } | null;
  harvestLoading: boolean;
  harvestError?: string | null;
}

const initialState: IReviewsState = {
  report: null,
  loading: false,
  error: null,
  lastCsvBlobUrl: null,
  lastXlsxBlobUrl: null,
  lastHarvest: null,
  harvestLoading: false,
  harvestError: null,
};

// --------- Thunks ----------
export const fetchReviewsReport = createAsyncThunk<
  ReportResponse,
  ReviewsFilters
>('reviews/fetchReviewsReport', async (filters) => {
  const { data } = await tryToGetReviewsReport(filters);
  return data as ReportResponse;
});

export const downloadReviewsCsv = createAsyncThunk<
  string, // returns object URL
  Omit<ReviewsFilters, 'sinceLastReport' | 'mark'>
>('reviews/downloadReviewsCsv', async (filters) => {
  const { data } = await tryToDownloadReviewsCsv(filters);
  const url = URL.createObjectURL(data);
  return url;
});

export const downloadReviewsXlsx = createAsyncThunk<
  string, // returns object URL
  ReviewsFilters
>('reviews/downloadReviewsXlsx', async (filters) => {
  const { data } = await tryToDownloadReviewsXlsx(filters);
  const url = URL.createObjectURL(data);
  return url;
});

export const harvestReviewsSince = createAsyncThunk<
  { inserted: number; skipped: number; since?: string },
  string // sinceISO
>('reviews/harvestReviewsSince', async (sinceISO) => {
  const { data } = await tryToHarvestNow(sinceISO);
  // expected shape: { ok: true, since: '...', inserted, skipped }
  return {
    inserted: Number(data?.inserted || 0),
    skipped: Number(data?.skipped || 0),
    since: data?.since,
  };
});

export const backfillReviewsMonths = createAsyncThunk<
  { inserted: number; skipped: number },
  number | undefined
>('reviews/backfillReviewsMonths', async (months = 1) => {
  const { data } = await tryToBackfillMonths(months);
  return {
    inserted: Number(data?.inserted || 0),
    skipped: Number(data?.skipped || 0),
  };
});

// --------- Slice ----------
export const reviewsSlice = createSlice({
  name: 'reviews',
  initialState,
  reducers: {
    revokeLastDownloads(state) {
      if (state.lastCsvBlobUrl) {
        URL.revokeObjectURL(state.lastCsvBlobUrl);
      }
      if (state.lastXlsxBlobUrl) {
        URL.revokeObjectURL(state.lastXlsxBlobUrl);
      }
      state.lastCsvBlobUrl = null;
      state.lastXlsxBlobUrl = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetch report
      .addCase(fetchReviewsReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReviewsReport.rejected, (state, action) => {
        state.loading = false;
        const err = action.error as unknown as AxiosError;
        state.error = (err?.message as string) || 'Failed to load reviews report';
      })
      .addCase(fetchReviewsReport.fulfilled, (state, action) => {
        state.loading = false;
        state.report = action.payload ?? null;
      })

      // download csv
      .addCase(downloadReviewsCsv.fulfilled, (state, action) => {
        // revoke previous to avoid leaks
        if (state.lastCsvBlobUrl) URL.revokeObjectURL(state.lastCsvBlobUrl);
        state.lastCsvBlobUrl = action.payload;
      })

      // download xlsx
      .addCase(downloadReviewsXlsx.fulfilled, (state, action) => {
        if (state.lastXlsxBlobUrl) URL.revokeObjectURL(state.lastXlsxBlobUrl);
        state.lastXlsxBlobUrl = action.payload;
      })

      // harvest since
      .addCase(harvestReviewsSince.pending, (state) => {
        state.harvestLoading = true;
        state.harvestError = null;
      })
      .addCase(harvestReviewsSince.rejected, (state, action) => {
        state.harvestLoading = false;
        const err = action.error as unknown as AxiosError;
        state.harvestError = (err?.message as string) || 'Harvest failed';
      })
      .addCase(harvestReviewsSince.fulfilled, (state, action) => {
        state.harvestLoading = false;
        state.lastHarvest = action.payload;
      })

      // backfill months
      .addCase(backfillReviewsMonths.pending, (state) => {
        state.harvestLoading = true;
        state.harvestError = null;
      })
      .addCase(backfillReviewsMonths.rejected, (state, action) => {
        state.harvestLoading = false;
        const err = action.error as unknown as AxiosError;
        state.harvestError = (err?.message as string) || 'Backfill failed';
      })
      .addCase(backfillReviewsMonths.fulfilled, (state, action) => {
        state.harvestLoading = false;
        state.lastHarvest = action.payload;
      });
  },
});

export const { revokeLastDownloads } = reviewsSlice.actions;
export default reviewsSlice.reducer;
